import React, { useEffect, useCallback, memo } from 'react';
import Portal from '../../Portal';

const FilterModal = memo(({ 
  showFilters, 
  setShowFilters, 
  filters,
  setFilters,
  handleStatusChange,
  handlePaymentChange,
  handleStartDateChange,
  handleEndDateChange,
  handleYearChange,
  handleCompareYearChange,
  compareYearOptions 
}) => {
  useEffect(() => {
    document.body.style.overflow = showFilters ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showFilters]);

  const handleClose = useCallback(() => setShowFilters(false), [setShowFilters]);
  
  const handleReset = useCallback(() => {
    setFilters({
      status: '',
      paymentMode: '',
      year: new Date().getFullYear(),
      compareYear: new Date().getFullYear() - 1,
      startDate: '',
      endDate: ''
    });
  }, [setFilters]);

  if (!showFilters) return null;

  return (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] overflow-hidden">
        <div className="bg-white shadow-2xl w-full h-full overflow-y-auto">
          <div className="bg-primaryRed px-4 py-2">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-medium text-white">Filter Analytics</h2>
                <p className="text-red-100 text-xs mt-0.5">Customize your dashboard view</p>
              </div>
              <button
                onClick={handleClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-4">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Quick Filters
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">Order Status</label>
                      <select 
                        value={filters.status} 
                        onChange={handleStatusChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value="">All Status</option>
                        <option value="Delivered">✅ Delivered</option>
                        <option value="Cancelled">❌ Cancelled</option>
                        <option value="Cancel">❌ Cancel</option>
                        <option value="running_order">🔄 Running</option>
                      </select>
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">Payment Method</label>
                      <select 
                        value={filters.paymentMode} 
                        onChange={handlePaymentChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value="">All Payment Methods</option>
                        <option value="UPI">💳 UPI</option>
                        <option value="COD">💰 Cash on Delivery</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Date Range
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">Start Date</label>
                      <input 
                        type="date" 
                        value={filters.startDate}
                        onChange={handleStartDateChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">End Date</label>
                      <input 
                        type="date" 
                        value={filters.endDate}
                        onChange={handleEndDateChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Year Comparison
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">Primary Year</label>
                      <input 
                        type="number" 
                        value={filters.year}
                        onChange={handleYearChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="2024"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">Compare With</label>
                      <select 
                        value={filters.compareYear} 
                        onChange={handleCompareYearChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        {compareYearOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-2">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <button
                onClick={handleReset}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Reset All
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-1.5 text-sm bg-primaryRed text-white rounded hover:bg-hoverRed font-medium transition-colors shadow-sm"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
});

export default FilterModal;
